import { AssignStatus } from "@/enum/list.enum";

import { ListPerson, ListPersonSchedule } from "@/pages/Private/pages/List/schema/list";

import { isCampaignStopped, isLeadInCampaign } from "./leadPanelActions.utils";

/**
 * Steps the pause endpoint deliberately leaves alone — mirrors `MANUAL_STEP_IDS`
 * (`apps/api/src/schedule/constants.ts`). An `n99` follow-up and the `n99-cond` invite-acceptance
 * gate inherit a `campaignId` from their thread but are user-scheduled, so
 * `pausePendingCampaignSchedules` skips them. A lead whose only queued row is one of these must
 * therefore not be offered "Pause outreach": the call would succeed having held nothing.
 */
export const MANUAL_STEP_IDS = ["n99", "n99-cond"];

/**
 * What the panel may do about this lead's campaign outreach — one total classification, so the
 * label and the action can never disagree (PREC-4107).
 */
export type OutreachHoldState =
	/** Work is queued that a pause would actually hold. */
	| "pausable"
	/** Outreach is held and can be put back in motion. */
	| "resumable"
	/** Nothing to hold and nothing to release. */
	| "none";

/**
 * A step the API's `outstandingCampaignSteps` predicate would move: campaign-driven, not run, not
 * cancelled, not failed. Exported because the optimistic update has to mirror exactly the rows the
 * server just held or released — a narrower or wider guess leaves the panel disagreeing with the
 * database it has already written to.
 */
export const isHoldableCampaignStep = (schedule: ListPersonSchedule): boolean =>
	!schedule.processedAt &&
	!schedule.canceledAt &&
	!schedule.failedAt &&
	!MANUAL_STEP_IDS.includes(schedule.stepId ?? "");

/** `active` picks the direction: `true` is work still on its way out, `false` is work held. */
const isOutstandingCampaignStep = (schedule: ListPersonSchedule, active: boolean): boolean =>
	schedule.active === active && isHoldableCampaignStep(schedule);

const hasOutstandingCampaignStep = (
	listPerson: ListPerson | null | undefined,
	active: boolean
): boolean =>
	(listPerson?.schedules ?? []).some((schedule) => isOutstandingCampaignStep(schedule, active));

/** Queued campaign work this lead still has coming. */
export const hasPendingCampaignStep = (listPerson?: ListPerson | null): boolean =>
	hasOutstandingCampaignStep(listPerson, true);

/** Campaign work already held — by the pause endpoint, or by a sender/campaign-wide hold. */
export const hasHeldCampaignStep = (listPerson?: ListPerson | null): boolean =>
	hasOutstandingCampaignStep(listPerson, false);

/**
 * The status-only fallback: `RUNNING` is written on the first *send*, so a lead assigned minutes
 * ago is still `ASSIGNED` while the server already counts its outreach as active. Both are
 * pausable — `stopCampaignAt` bars the campaign from creating the step even when no row exists yet
 * (PREC-3906).
 */
const PAUSABLE_STATUSES: AssignStatus[] = [AssignStatus.ASSIGNED, AssignStatus.RUNNING];

/**
 * Decide, from the row the panel holds, whether this lead's outreach can be paused or resumed.
 *
 * **Evidence first, status second.** The schedules answer the question the user is really asking —
 * "is something going to be sent to this person?" — and they are the only half of it that every
 * surface carries: `list-person-info.dto.ts` serves `schedules` and `campaignId` but **not**
 * `stopCampaignAt`, so a classification resting on the cut-off alone is silently dead on the Outbox
 * (web CLAUDE.md §17). Status is the fallback for the surfaces whose payload has no schedules at
 * all (Contacts, the extension's detail row), which is exactly where the pre-PREC-4107 rule was
 * written and still holds.
 *
 * Pausable outranks resumable on a mixed row: some steps held and others still live means outreach
 * is still going out, and stopping it is the more urgent of the two offers.
 */
export const resolveOutreachHoldState = (
	listPerson?: ListPerson | null,
	now: Date = new Date()
): OutreachHoldState => {
	if (hasPendingCampaignStep(listPerson)) {
		return "pausable";
	}

	if (hasHeldCampaignStep(listPerson)) {
		return "resumable";
	}

	// No schedule evidence either way — fall back to what the row's own columns say.
	if (!isLeadInCampaign(listPerson)) {
		return "none";
	}

	if (isCampaignStopped(listPerson?.stopCampaignAt, now)) {
		return "resumable";
	}

	return listPerson?.assignStatus && PAUSABLE_STATUSES.includes(listPerson.assignStatus)
		? "pausable"
		: "none";
};
